import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import {
  DayView,
  FULL_DAY_VIEW,
  slotLabelForRow,
} from "../dayGridHelpers";
import { colorTextDisabled, colorTextMuted } from "src/design/tokens";

interface TimeAxisProps {
  /** Rows to render; defaults to the whole day. */
  view?: DayView;
  /** Rows currently under a drag, highlighted so the drop time is readable. */
  highlightedRows?: { startRow: number; endRow: number } | null;
  sx?: SxProps;
}

/**
 * The hour gutter down the left of the grid. Every row gets its own band so the
 * axis lines up with the cells exactly, but only whole hours are labelled —
 * 48 labels in one screen height would be unreadable.
 */
export const TimeAxis = ({
  view = FULL_DAY_VIEW,
  highlightedRows,
  sx,
}: TimeAxisProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      ...sx,
    }}
  >
    {Array.from({ length: view.endRow - view.startRow }, (_, index) => {
      const row = view.startRow + index;
      const isFullHour = row % 2 === 0;
      const isHighlighted =
        !!highlightedRows &&
        row >= highlightedRows.startRow &&
        row < highlightedRows.endRow;

      return (
        <Box
          key={`axis-${row}`}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: 0.75,
          }}
        >
          {(isFullHour || isHighlighted) && (
            <Typography
              sx={{
                fontSize: 9.5,
                lineHeight: 1,
                fontWeight: isHighlighted ? 700 : 500,
                color: isHighlighted
                  ? colorTextMuted
                  : isFullHour
                    ? colorTextMuted
                    : colorTextDisabled,
              }}
            >
              {slotLabelForRow(row)}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);
